import {
  AlertTriangle,
  Bell,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Fingerprint,
  HelpCircle,
  Home,
  MessageSquare,
  Play,
  ScanFace,
  Syringe,
  UserRound,
} from "lucide-react";

const assetPath = (fileName) => `${process.env.PUBLIC_URL}/images/${fileName}`;

const patientThemeVars = {
  "--patient-primary": "#FFDD00",
  "--patient-warning": "#E2B93B",
  "--patient-text": "#282828",
  "--patient-muted": "#4F4F4F",
  "--patient-subtle": "#828282",
  "--patient-border": "#F6F6F6",
  "--patient-border-strong": "#BDBDBD",
  "--patient-surface": "#FCFCFC",
  "--patient-link": "#8E6F10",
};

const patientAssets = {
  splash: assetPath("patient-flow-splash.png"),
  logo: assetPath("patient-flow-esya-logo.png"),
  testimonial: assetPath("patient-flow-testimonial.png"),
  success: assetPath("patient-flow-success.png"),
  gestaltLogo: assetPath("gestalt-logo.svg"),
};

const flowSteps = [
  {
    label: "Splash",
    title: "Launch and brand reveal",
    description: "Patient-first entry screen with care reminders and powered-by branding.",
  },
  {
    label: "MPIN",
    title: "Secure login",
    description: "Four-digit MPIN with reset path and biometric alternatives.",
  },
  {
    label: "Dashboard",
    title: "Care dashboard",
    description: "Appointments, reports, stories, and quick links in one mobile home.",
  },
];

const pinSlots = ["1", "2", "3", "4"];

const bottomNavItems = [
  { label: "Home", Icon: Home, active: true },
  { label: "Appointment", Icon: CalendarDays },
  { label: "Services", Icon: Briefcase },
  { label: "My Account", Icon: UserRound },
];

function StatusBar() {
  return (
    <div className="flex h-[52px] items-end justify-between bg-white px-6 py-2.5 text-sm font-semibold text-[#1d1b20]">
      <span>9:30</span>
      <span className="absolute left-1/2 top-[18px] h-6 w-6 -translate-x-1/2 rounded-full bg-[#1d1b20]" />
      <div className="flex items-center gap-1.5" aria-hidden>
        <span className="h-3 w-4 rounded-sm bg-slate-200 [clip-path:polygon(0_70%,100%_0,100%_100%,0_100%)]" />
        <span className="h-4 w-4 bg-[#1d1b20] [clip-path:polygon(0_100%,100%_0,100%_100%)]" />
        <span className="h-4 w-2 rounded-sm bg-[#1d1b20]" />
      </div>
    </div>
  );
}

function GestureBar({ className = "" }) {
  return (
    <div className={`flex h-6 items-center justify-center bg-white ${className}`} aria-hidden>
      <span className="h-1 w-[108px] rounded-full bg-[#1d1b20]" />
    </div>
  );
}

function PhoneFrame({ label, children, className = "" }) {
  return (
    <article className="mx-auto w-full max-w-[412px]" aria-label={label}>
      <div className="mb-4 flex items-center justify-between gap-3 text-white">
        <h2 className="font-display text-lg font-bold">{label}</h2>
        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white/75">
          412 x mobile
        </span>
      </div>
      <div
        className={`relative h-[760px] overflow-hidden rounded-[34px] border border-white/20 bg-[var(--patient-surface)] shadow-2xl shadow-black/30 ring-8 ring-slate-950/70 ${className}`}
        style={patientThemeVars}
      >
        {children}
      </div>
    </article>
  );
}

function BrandLockup({ compact = false }) {
  return (
    <div className="flex flex-col items-center text-center">
      <img
        src={patientAssets.logo}
        alt="eSya Patient Care"
        className={compact ? "h-28 w-28 object-contain" : "h-32 w-32 object-contain"}
        loading="lazy"
        decoding="async"
      />
      <p className="mt-7 text-2xl font-bold leading-[26px] tracking-[-0.01em] text-[var(--patient-text)]">
        eSya Patient Care
      </p>
      <p className="mt-2 text-base leading-6 tracking-[0.01em] text-[var(--patient-muted)]">
        Empowering Patients<span className="text-[var(--patient-primary)]">.</span>
      </p>
    </div>
  );
}

function ReminderChip({ Icon, children, className = "", tone = "text-[var(--patient-subtle)]" }) {
  return (
    <div
      className={`absolute z-20 flex items-center gap-3 rounded bg-white px-3 py-2 text-base shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-1 ring-[var(--patient-border)] ${className}`}
    >
      <Icon className={`h-5 w-5 ${tone}`} strokeWidth={1.8} />
      <span className="whitespace-nowrap leading-6 tracking-[0.01em] text-[var(--patient-subtle)]">{children}</span>
    </div>
  );
}

function SplashPhone() {
  return (
    <PhoneFrame label="Splash screen">
      <StatusBar />
      <div className="relative h-[684px] overflow-hidden bg-white">
        <ReminderChip Icon={Bell} className="left-[66px] top-5 -rotate-[5deg]" tone="text-emerald-500">
          1 Up Coming appointment
        </ReminderChip>
        <ReminderChip Icon={AlertTriangle} className="right-20 top-[74px] rotate-[5deg]" tone="text-red-400">
          Payment dues
        </ReminderChip>
        <ReminderChip Icon={Syringe} className="left-11 top-[154px] -rotate-[8deg]" tone="text-[var(--patient-warning)]">
          Vaccination due
        </ReminderChip>

        <div className="absolute inset-x-0 top-[134px] h-[502px]">
          <img
            src={patientAssets.splash}
            alt="Smiling care team"
            className="h-full w-full object-cover object-center"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-b from-transparent to-[#fff4af]" />
        </div>

        <div className="absolute right-6 top-[325px] z-30 w-[217px] -rotate-[5deg] rounded bg-white p-4 shadow-[0_4px_18px_rgba(0,0,0,0.2)] ring-1 ring-[var(--patient-border)]">
          <p className="text-base leading-6 text-black">Appointments</p>
          <div className="mt-2 flex gap-3 border-b border-[var(--patient-border-strong)] text-[10px] leading-5">
            <span className="border-b-2 border-[var(--patient-primary)] text-black">Upcoming</span>
            <span className="text-[var(--patient-subtle)]">Completed</span>
            <span className="text-[var(--patient-subtle)]">Cancelled</span>
          </div>
          {[0, 1].map((item) => (
            <div key={item} className="mt-3 flex items-center gap-3">
              <span className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-100 to-rose-100 ring-1 ring-slate-200" />
              <span className="flex-1 space-y-1.5">
                <span className="block h-1.5 w-16 rounded-full bg-slate-300" />
                <span className="block h-1.5 w-24 rounded-full bg-slate-300" />
              </span>
              <CheckCircle2 className="h-6 w-6 fill-lime-300 text-lime-600" strokeWidth={1.8} />
            </div>
          ))}
        </div>

        <div className="absolute inset-x-[35px] bottom-0 z-30 rounded-t-2xl bg-white px-4 pb-5 pt-4 shadow-[0_-8px_22px_rgba(15,23,42,0.08)]">
          <BrandLockup />
          <div className="mt-9 h-1 rounded-full bg-[var(--patient-border)]">
            <div className="h-full w-2/5 rounded-full bg-[var(--patient-primary)]" />
          </div>
        </div>
      </div>
      <div className="flex h-[52px] items-start justify-center gap-2 bg-white pt-2 text-base text-[var(--patient-subtle)]">
        <span>Powered by</span>
        <img src={patientAssets.gestaltLogo} alt="Gestalt" className="mt-0.5 h-5 w-auto" loading="lazy" />
      </div>
      <GestureBar />
    </PhoneFrame>
  );
}

function MpinPhone() {
  return (
    <PhoneFrame label="Set MPIN">
      <StatusBar />
      <div className="flex h-[684px] flex-col bg-[var(--patient-surface)] px-4 pb-9 pt-14">
        <BrandLockup compact />

        <section className="mt-9">
          <h2 className="text-xl font-bold leading-7 text-[var(--patient-muted)]">Enter Your MPIN</h2>
          <div className="mt-4 flex justify-center gap-8 rounded-lg border border-[var(--patient-border)] bg-white p-4">
            {pinSlots.map((slot) => (
              <div
                key={slot}
                className="flex h-12 w-12 items-center justify-center rounded-lg border border-[var(--patient-warning)] bg-white pt-1 text-center text-base text-[#333]"
              >
                *
              </div>
            ))}
          </div>
        </section>

        <button className="mt-8 h-16 w-full rounded-lg border border-[var(--patient-warning)] bg-[var(--patient-primary)] text-base font-bold tracking-[0.01em] text-black transition hover:brightness-105">
          Login
        </button>

        <p className="mt-8 text-center text-base leading-6 tracking-[0.01em] text-[#333]">
          Forgot MPIN?{" "}
          <a href="#reset-mpin" className="text-sm font-semibold text-[var(--patient-link)]">
            Reset.
          </a>
        </p>

        <div className="mt-10 flex items-center justify-center gap-3 text-base leading-6 text-black">
          <span className="h-px w-[92px] bg-[#333]" />
          <span>or</span>
          <span className="h-px w-[92px] bg-[#333]" />
        </div>

        <section className="mt-9">
          <h2 className="text-xl font-bold leading-7 text-[var(--patient-muted)]">Bio Metric Login</h2>
          <div className="mt-4 rounded-lg border border-[var(--patient-border)] bg-white p-4 shadow-[0_5px_8px_rgba(189,189,189,0.08)]">
            <div className="grid grid-cols-2 overflow-hidden rounded border border-[var(--patient-border-strong)] bg-[var(--patient-border)]">
              <button className="flex items-center justify-center gap-4 border-r border-[var(--patient-border-strong)] px-4 py-3 text-base font-semibold leading-[22px] text-black">
                <Fingerprint className="h-7 w-7" strokeWidth={1.8} />
                Fingerprint
              </button>
              <button className="flex items-center justify-center gap-4 px-4 py-3 text-base font-semibold leading-[22px] text-black">
                <ScanFace className="h-7 w-7" strokeWidth={1.8} />
                Face ID
              </button>
            </div>
          </div>
        </section>
      </div>
      <GestureBar />
    </PhoneFrame>
  );
}

function AlertRow({ kind, title, subtitle, Icon }) {
  return (
    <div className="flex h-[88px] items-center justify-between border border-[var(--patient-border-strong)] bg-white first:rounded-t-lg last:-mt-px last:rounded-b-lg">
      <div className="flex min-w-0 flex-1 items-center gap-6">
        <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-l-lg bg-[var(--patient-border)]">
          {kind === "date" ? (
            <span className="text-center text-xs font-medium leading-[1.4] tracking-[0.01em] text-[#333]">
              30th
              <br />
              Nov
            </span>
          ) : (
            <Icon className="h-6 w-6 text-[#333]" strokeWidth={1.8} />
          )}
        </div>
        <div className="min-w-0 pr-2">
          {subtitle ? <p className="text-xs font-medium leading-[1.4] tracking-[0.01em] text-[var(--patient-muted)]">{subtitle}</p> : null}
          <p className={`${subtitle ? "text-sm font-bold leading-5 text-[#333]" : "text-xs font-medium leading-[1.4] text-[var(--patient-muted)]"}`}>
            {title}
          </p>
        </div>
      </div>
      <ChevronRight className="mr-4 h-5 w-5 shrink-0 text-black" strokeWidth={1.8} />
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold leading-7 text-[var(--patient-muted)]">{title}</h2>
      <a href={`#${title.toLowerCase().replaceAll(" ", "-")}`} className="text-sm leading-[1.4] text-[var(--patient-link)] underline">
        View All
      </a>
    </div>
  );
}

function CarouselDots() {
  return (
    <div className="flex items-center justify-center gap-2 py-2 text-[var(--patient-border-strong)]">
      <ChevronRight className="h-4 w-4 rotate-180" strokeWidth={1.8} />
      <span className="h-2 w-4 rounded-full bg-[var(--patient-primary)]" />
      {[0, 1, 2, 3].map((dot) => (
        <span key={dot} className="h-1 w-1 rounded-full bg-[var(--patient-border-strong)]" />
      ))}
      <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
    </div>
  );
}

function BottomNav() {
  return (
    <nav className="absolute inset-x-0 bottom-6 z-30 flex h-20 items-center justify-between border-t border-[var(--patient-border)] bg-white px-4 py-2">
      {bottomNavItems.map(({ label, Icon, active }) => (
        <a
          key={label}
          href={`#${label.toLowerCase().replace(" ", "-")}`}
          className={`flex h-16 w-[72px] flex-col items-center justify-center gap-1 rounded px-1 py-2 text-[10px] leading-[19.6px] ${
            active ? "bg-[var(--patient-primary)] text-black" : "text-[var(--patient-subtle)]"
          }`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.8} />
          <span>{label}</span>
        </a>
      ))}
    </nav>
  );
}

function DashboardPhone() {
  return (
    <PhoneFrame label="Patient Dashboard" className="h-[820px]">
      <StatusBar />
      <header className="flex h-[74px] items-center justify-between border-b border-[var(--patient-primary)] bg-white px-4">
        <div className="flex items-center gap-4">
          <img src={patientAssets.logo} alt="" className="h-[42px] w-[42px] object-contain" loading="lazy" />
          <h2 className="text-xl font-bold leading-7 text-[var(--patient-muted)]">Home</h2>
        </div>
        <div className="flex items-center gap-4 text-[var(--patient-muted)]">
          <Bell className="h-5 w-5" strokeWidth={1.8} />
          <HelpCircle className="h-5 w-5" strokeWidth={1.8} />
        </div>
      </header>

      <div className="h-[668px] overflow-y-auto px-4 pb-28 pt-6">
        <h1 className="text-xl font-bold leading-7 text-black">Hi, Kanda</h1>
        <div className="mt-4">
          <AlertRow kind="date" subtitle="Your upcoming appointment with" title="Dr. Ahmed." Icon={CalendarDays} />
          <AlertRow title="Your lab report is ready for review" Icon={MessageSquare} />
        </div>

        <section className="mt-8 text-center">
          <h2 className="text-2xl font-bold leading-[26px] tracking-[-0.01em] text-black">
            Your Health, in Your
            <br />
            Hands
          </h2>
          <p className="mt-8 text-lg leading-[26px] text-black">
            Easily book appointments and connect with your doctors, anytime, anywhere.
          </p>
          <button className="mt-8 flex h-[88px] w-full items-center justify-center gap-4 rounded-lg border-2 border-[var(--patient-warning)] bg-[var(--patient-primary)] text-sm font-bold leading-5 tracking-[0.01em] text-black">
            <CalendarDays className="h-5 w-5" strokeWidth={1.8} />
            Book Appointment
          </button>
        </section>

        <section className="mt-7">
          <SectionHeader title="Testimonials" />
          <div className="mt-4 rounded-lg border border-[var(--patient-border)] bg-white p-4 shadow-[0_5px_12px_rgba(0,0,0,0.08)]">
            <div className="relative aspect-video overflow-hidden rounded-sm">
              <img
                src={patientAssets.testimonial}
                alt="Patient testimonial video"
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-transparent" />
              <p className="absolute left-4 top-3 text-lg leading-[1.25] text-white">Made at Theorem with love</p>
              <span className="absolute left-1/2 top-1/2 flex h-12 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-black/70 text-white">
                <Play className="h-6 w-6 fill-white" />
              </span>
            </div>
            <p className="mt-2 text-sm font-bold leading-5 tracking-[0.01em] text-[var(--patient-muted)]">
              Gest Alt has completely transformed how we manage patient data.
            </p>
            <p className="mt-1 text-sm leading-[1.4] text-[var(--patient-muted)]">Mr. Kumar</p>
          </div>
          <CarouselDots />
        </section>

        <section className="mt-7">
          <SectionHeader title="Success Stories" />
          <div className="mt-4 rounded-lg border border-[var(--patient-border)] bg-white p-4 shadow-[0_5px_12px_rgba(0,0,0,0.08)]">
            <img
              src={patientAssets.success}
              alt="Success story before and after"
              className="h-[182px] w-full rounded-sm object-cover object-top"
              loading="lazy"
              decoding="async"
            />
          </div>
          <CarouselDots />
        </section>

        <section className="mt-7 p-4">
          <h2 className="text-xl font-bold leading-7 text-[var(--patient-muted)]">Quick Links</h2>
          {["About GestAlt", "FAQs"].map((item) => (
            <div key={item} className="mt-2 flex items-center justify-between border-b border-[var(--patient-border)] py-2 text-sm">
              <span className="text-[var(--patient-muted)]">{item}</span>
              <a href={`#${item.toLowerCase()}`} className="text-[var(--patient-link)] underline">
                View
              </a>
            </div>
          ))}
        </section>
      </div>
      <BottomNav />
      <GestureBar className="absolute inset-x-0 bottom-0 z-30" />
    </PhoneFrame>
  );
}

export default function PatientAppFlow() {
  return (
    <main className="min-h-screen bg-slate-950 text-white" style={patientThemeVars}>
      <section className="relative isolate overflow-hidden px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(255,221,0,0.18),transparent_28%),radial-gradient(circle_at_82%_18%,rgba(20,184,166,0.18),transparent_28%)]" />
        <div className="mx-auto max-w-7xl">
          <a
            href="/"
            className="inline-flex rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/15"
          >
            Back to website
          </a>

          <div className="mt-10 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <div>
              <p className="inline-flex rounded-full border border-yellow-300/40 bg-yellow-300/10 px-3.5 py-1.5 text-sm font-semibold text-[var(--patient-primary)]">
                eSya patient app flow
              </p>
              <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Patient onboarding and home experience.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-8 text-slate-300">
                Built from the linked Figma flow with shared design variables for color, spacing, borders, and recurring mobile UI patterns.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {flowSteps.map((step, index) => (
                <div key={step.label} className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--patient-primary)] text-sm font-bold text-black">
                    {index + 1}
                  </span>
                  <h2 className="mt-4 font-display text-lg font-bold">{step.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 grid gap-10 xl:grid-cols-3">
            <SplashPhone />
            <MpinPhone />
            <DashboardPhone />
          </div>
        </div>
      </section>
    </main>
  );
}
